import { useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import { customerService } from '../services/firebase';
import { Customer, CustomerForm as CustomerFormType } from '../types';
import LoadingSpinner from './LoadingSpinner';
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';

interface CustomerFormProps {
  customer?: Customer;
}

export default function CustomerForm({ customer }: CustomerFormProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CustomerFormType>({
    defaultValues: customer || {
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'USA',
      },
    },
  });

  const onSubmit: SubmitHandler<CustomerFormType> = async (data) => {
    if (!user) {
      toast.error('You must be logged in to modify customer data.');
      return;
    }

    try {
      if (customer) {
        await customerService.updateCustomer(customer.id, data);
        toast.success('Customer updated successfully!');
      } else {
        await customerService.createCustomer(data, user.id);
        toast.success('Customer created successfully!');
      }
      navigate('/customers');
    } catch (error) {
      console.error('Error saving customer:', error);
      toast.error('Failed to save customer. Please try again.');
    }
  };

  const renderError = (message: string | undefined) =>
    message && <p className="mt-1 text-sm text-red-600">{message}</p>;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8 bg-white p-8 rounded-lg shadow-md">
      {/* Personal Information */}
      <div className="space-y-6">
        <h2 className="text-xl font-semibold border-b pb-2">Personal Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">Full Name</label>
            <div className="mt-1 relative">
              <UserIcon className="pointer-events-none absolute top-1/2 -translate-y-1/2 left-3 h-5 w-5 text-gray-400" />
              <input {...register('name', { required: 'Name is required' })} type="text" id="name" className="input pl-10" autoComplete="name" />
            </div>
            {renderError(errors.name?.message)}
          </div>
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email Address</label>
            <div className="mt-1 relative">
              <EnvelopeIcon className="pointer-events-none absolute top-1/2 -translate-y-1/2 left-3 h-5 w-5 text-gray-400" />
              <input {...register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+$/i, message: "Invalid email format" } })} type="email" id="email" className="input pl-10" autoComplete="email" />
            </div>
            {renderError(errors.email?.message)}
          </div>
          {/* Phone */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Phone Number</label>
            <div className="mt-1 relative">
              <PhoneIcon className="pointer-events-none absolute top-1/2 -translate-y-1/2 left-3 h-5 w-5 text-gray-400" />
              <input {...register('phone', { required: 'Phone is required' })} type="tel" id="phone" className="input pl-10" autoComplete="tel" />
            </div>
            {renderError(errors.phone?.message)}
          </div>
        </div>
      </div>

      {/* Address Information */}
      <div className="space-y-6">
        <h2 className="text-xl font-semibold border-b pb-2">Address</h2>
        {/* Street */}
        <div>
          <label htmlFor="street" className="block text-sm font-medium text-gray-700">Street Address</label>
          <div className="mt-1 relative">
            <MapPinIcon className="pointer-events-none absolute top-1/2 -translate-y-1/2 left-3 h-5 w-5 text-gray-400" />
            <input {...register('address.street', { required: 'Street is required' })} type="text" id="street" className="input pl-10" autoComplete="street-address" />
          </div>
          {renderError(errors.address?.street?.message)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* City */}
          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700">City</label>
            <input {...register('address.city', { required: 'City is required' })} type="text" id="city" className="input mt-1" autoComplete="address-level2" />
            {renderError(errors.address?.city?.message)}
          </div>
          {/* State */}
          <div>
            <label htmlFor="state" className="block text-sm font-medium text-gray-700">State / Province</label>
            <input {...register('address.state', { required: 'State is required' })} type="text" id="state" className="input mt-1" autoComplete="address-level1" />
            {renderError(errors.address?.state?.message)}
          </div>
          {/* Zip Code */}
          <div>
            <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700">Zip / Postal Code</label>
            <input {...register('address.zipCode', { required: 'Zip code is required' })} type="text" id="zipCode" className="input mt-1" autoComplete="postal-code" />
            {renderError(errors.address?.zipCode?.message)}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-4 pt-4 border-t">
        <button type="button" onClick={() => navigate(-1)} className="btn-outline">
          Cancel
        </button>
        <button type="submit" disabled={isSubmitting} className="btn-primary flex items-center">
          {isSubmitting && <LoadingSpinner />}
          {isSubmitting ? 'Saving...' : (customer ? 'Update Customer' : 'Create Customer')}
        </button>
      </div>
    </form>
  );
} 